import { Injectable, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, merge, of } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { setNetworkStatus } from '../../store/ui/ui.actions';
import { CacheService } from './cache.service';

@Injectable({ providedIn: 'root' })
export class NetworkService implements OnDestroy {
  private store = inject(Store);
  private cacheService = inject(CacheService);

  private isOnline$ = new BehaviorSubject<boolean>(navigator.onLine);
  private pendingSyncQueue: Array<() => Promise<void>> = [];

  constructor() {
    this.initNetworkListeners();
  }

  private initNetworkListeners(): void {
    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

    merge(online$, offline$).pipe(
      distinctUntilChanged()
    ).subscribe(isOnline => {
      this.isOnline$.next(isOnline);
      this.store.dispatch(setNetworkStatus({ isOnline }));
      if (isOnline) this.flushSyncQueue();
    });
  }

  getIsOnline(): Observable<boolean> {
    return this.isOnline$.asObservable();
  }

  get isOnline(): boolean {
    return this.isOnline$.value;
  }

  /**
   * Queue an action to be executed when the network comes back online.
   * If already online, executes immediately.
   */
  queueForSync(action: () => Promise<void>): void {
    if (this.isOnline) {
      action().catch(console.error);
    } else {
      this.pendingSyncQueue.push(action);
    }
  }

  private async flushSyncQueue(): Promise<void> {
    const queue = [...this.pendingSyncQueue];
    this.pendingSyncQueue = [];
    for (const action of queue) {
      try { await action(); } catch (e) { console.error('Sync error:', e); }
    }
  }

  ngOnDestroy(): void {
    this.isOnline$.complete();
  }
}
